import { useEffect, useState, type JSX } from 'react';
import { z } from 'zod';
import { businessActionSchema, uuidV7Schema } from '@cryptology-assist/domain';
import { request } from './api';
import { ActionSummary, ErrorNotice, useMutation } from './components';
import type { Copy, Language } from './copy';

const confirmationSchema = z.object({
  id: uuidV7Schema,
  request: businessActionSchema,
  status: z.string(),
});
const claimsSchema = z
  .object({
    companyId: uuidV7Schema,
    botId: uuidV7Schema,
    actionId: uuidV7Schema,
    token: z.string().min(20).max(200),
  })
  .strict();

export function Confirmation({
  copy,
  language,
}: {
  copy: Copy;
  language: Language;
}): JSX.Element {
  const [claims] = useState(() =>
    claimsSchema.safeParse(
      Object.fromEntries(new URLSearchParams(window.location.hash.slice(1)))
    )
  );
  const [action, setAction] = useState<z.infer<
      typeof confirmationSchema
    > | null>(null),
    [done, setDone] = useState(false);
  const mutation = useMutation();
  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
    if (!claims.success) return;
    const input = claims.data;
    void mutation.run(async () =>
      setAction(
        await request('/v1/confirm', confirmationSchema, {
          body: {
            scope: { companyId: input.companyId, botId: input.botId },
            actionId: input.actionId,
            token: input.token,
            accept: false,
          },
        })
      )
    );
  }, [claims]);
  return (
    <main className="confirmation-page">
      <p className="brand">{copy.product}</p>
      <h1>{copy.confirmationTitle}</h1>
      {!claims.success ? (
        <p>{copy.invalidLink}</p>
      ) : done ? (
        <p className="notice success" role="status">
          {copy.confirmationSent}
        </p>
      ) : (
        <>
          <p className="muted">{copy.confirmationHelp}</p>
          <ErrorNotice error={mutation.error} copy={copy} />
          {mutation.busy && !action && (
            <p className="loading">{copy.loading}</p>
          )}
          {action && (
            <>
              <ActionSummary
                action={action.request}
                copy={copy}
                language={language}
              />
              <button
                className="primary"
                disabled={mutation.busy}
                onClick={() =>
                  void mutation.run(async () => {
                    const input = claims.data;
                    await request(
                      '/v1/confirm',
                      z.object({
                        accepted: z.literal(true),
                        status: z.literal('pending'),
                      }),
                      {
                        body: {
                          scope: {
                            companyId: input.companyId,
                            botId: input.botId,
                          },
                          actionId: input.actionId,
                          token: input.token,
                          accept: true,
                        },
                      }
                    );
                    setDone(true);
                  })
                }
              >
                {copy.confirm}
              </button>
            </>
          )}
        </>
      )}
    </main>
  );
}
